import type { FieldKey, ViewColumn } from './types';

/**
 * Edits to a view's columns.
 *
 * The model has two ideas — which columns exist, and which fields sit in each, in order — so four
 * operations cover everything a user can do. Each one first takes the field out of wherever it was:
 * a field lives in one place, so "add" and "move" are the same action, and no edit can leave a value
 * rendered twice. Ported from the RD-2957 workspace PoC.
 */

export const newColumnId = (): string => `col_${Math.random().toString(36).slice(2, 10)}`;

const withoutKeys = (columns: ViewColumn[], keys: FieldKey[]): ViewColumn[] =>
  columns
    .map(column => ({ ...column, members: column.members.filter(member => !keys.includes(member)) }))
    // A column emptied by the removal disappears rather than lingering as a blank strip.
    .filter(column => column.members.length > 0);

/** Drops a field into an existing column at a position, or at its end. */
export const placeInColumn = (columns: ViewColumn[], key: FieldKey, targetColumnId: string, atIndex?: number): ViewColumn[] => {
  if (!columns.some(column => column.id === targetColumnId)) return columns;
  const source = columns.find(column => column.members.includes(key));
  const sameColumn = source?.id === targetColumnId;
  const originalIndex = sameColumn ? source.members.indexOf(key) : -1;

  // The target column must survive even when the dragged field was its only member and is dropped
  // back into it — so it is rebuilt from the original, not from the pruned list.
  return columns
    .map(column => {
      if (column.id === targetColumnId) {
        const members = column.members.filter(member => member !== key);
        // The drop index was measured with the dragged row still in the list, so moving it down
        // within its own column would land one slot too far.
        const raw = atIndex ?? members.length + (sameColumn ? 1 : 0);
        const index = sameColumn && raw > originalIndex ? raw - 1 : raw;
        members.splice(Math.max(0, Math.min(members.length, index)), 0, key);
        return { ...column, members };
      }
      return { ...column, members: column.members.filter(member => member !== key) };
    })
    .filter(column => column.members.length > 0);
};

/** Gives fields a column of their own (stacked in the given order), inserted among the columns. */
export const placeAsNewColumn = (columns: ViewColumn[], keys: FieldKey[], atIndex?: number, id: string = newColumnId()): ViewColumn[] => {
  if (keys.length === 0) return columns;
  // The insert position counts the columns as the user saw them; a column that disappears because
  // the move emptied it shifts everything after it left by one.
  const before = columns.slice(0, Math.max(0, atIndex ?? columns.length));
  const emptiedBefore = before.filter(column => column.members.every(member => keys.includes(member))).length;
  const remaining = withoutKeys(columns, keys);
  const index = Math.max(0, Math.min(remaining.length, (atIndex ?? remaining.length + emptiedBefore) - emptiedBefore));
  return [...remaining.slice(0, index), { id, members: [...keys] }, ...remaining.slice(index)];
};

/** Takes a field off the table; it goes back to the available ones. */
export const removeField = (columns: ViewColumn[], key: FieldKey): ViewColumn[] => withoutKeys(columns, [key]);

/** Removes a whole column; its fields go back to the available ones. */
export const removeColumn = (columns: ViewColumn[], id: string): ViewColumn[] => columns.filter(column => column.id !== id);

/** Reorders the columns. `atIndex` is a gap position (0 = before the first column). */
export const moveColumnTo = (columns: ViewColumn[], id: string, atIndex: number): ViewColumn[] => {
  const from = columns.findIndex(column => column.id === id);
  if (from === -1) return columns;
  const without = columns.filter(column => column.id !== id);
  const index = Math.max(0, Math.min(without.length, atIndex > from ? atIndex - 1 : atIndex));
  return [...without.slice(0, index), columns[from], ...without.slice(index)];
};

/** Every field placed somewhere, in column-then-member order. */
export const placedKeys = (columns: ViewColumn[]): FieldKey[] => columns.flatMap(column => column.members);

/**
 * A preset (e.g. "Loading – place, date, time") counts as switched on only while exactly its stack
 * exists as one column. Its fields scattered over other columns are those fields, not the preset.
 */
export const isPresetOn = (columns: ViewColumn[], preset: FieldKey[]): boolean =>
  columns.some(column => column.members.length === preset.length && column.members.every((member, i) => member === preset[i]));

/**
 * Switches a preset on: its stack becomes a new last column and takes its fields from wherever they
 * were — the same "add = move" rule as dragging, so a value still shows only once.
 */
export const applyPreset = (columns: ViewColumn[], preset: FieldKey[], id?: string): ViewColumn[] =>
  isPresetOn(columns, preset) ? columns : placeAsNewColumn(columns, preset, undefined, id);

/** Switches a preset off: the column holding exactly its stack goes, with its fields. */
export const removePreset = (columns: ViewColumn[], preset: FieldKey[]): ViewColumn[] =>
  columns.filter(column => !(column.members.length === preset.length && column.members.every((member, i) => member === preset[i])));

/**
 * Reconciles stored columns with the fields this build knows.
 *
 * Stored data may come from an older or newer release, or a hand-edited entry: unknown keys are
 * dropped, a key placed twice keeps only its first placement, emptied columns disappear, and a result
 * with nothing left falls back — a table with no columns can't be fixed from the UI.
 */
export const sanitizeColumns = (raw: unknown, knownKeys: FieldKey[], fallback: () => ViewColumn[]): ViewColumn[] => {
  const known = new Set(knownKeys);
  const placed = new Set<FieldKey>();
  const columns: ViewColumn[] = [];

  (Array.isArray(raw) ? raw : []).forEach(entry => {
    const members: unknown[] = Array.isArray((entry as ViewColumn)?.members) ? (entry as ViewColumn).members : [];
    const kept = members.filter((key): key is FieldKey => {
      if (typeof key !== 'string' || !known.has(key) || placed.has(key)) return false;
      placed.add(key);
      return true;
    });
    const id = typeof (entry as ViewColumn)?.id === 'string' && (entry as ViewColumn).id ? (entry as ViewColumn).id : newColumnId();
    const title = normalizeColumnTitle((entry as ViewColumn)?.title);
    const width = normalizeColumnWidth((entry as ViewColumn)?.width);
    if (kept.length > 0) columns.push({ id, members: kept, ...(title ? { title } : {}), ...(width ? { width } : {}) });
  });

  return columns.length > 0 ? columns : fallback();
};

/** Columns built from member lists, e.g. a table's default layout. */
export const columnsFrom = (stacks: FieldKey[][]): ViewColumn[] => stacks.map(members => ({ id: newColumnId(), members: [...members] }));

export const MAX_COLUMN_TITLE_LENGTH = 60;

/** A heading worth storing: trimmed, inner whitespace collapsed, capped; blank means "use the derived one". */
export const normalizeColumnTitle = (title: unknown): string | undefined => {
  if (typeof title !== 'string') return undefined;
  const clean = title.trim().replace(/\s+/g, ' ').slice(0, MAX_COLUMN_TITLE_LENGTH);
  return clean.length > 0 ? clean : undefined;
};

/** Gives a column the user's own heading, or — for a blank one — takes it back to the derived heading. */
export const renameColumn = (columns: ViewColumn[], id: string, title: string): ViewColumn[] =>
  columns.map(column => {
    if (column.id !== id) return column;
    const clean = normalizeColumnTitle(title);
    const { title: _previous, ...rest } = column;
    return clean ? { ...rest, title: clean } : rest;
  });

/** Narrowest and widest a user can drag a column; outside that a column is unusable or swallows the table. */
export const MIN_COLUMN_WIDTH = 60;
export const MAX_COLUMN_WIDTH = 1200;

/** A width worth storing: a finite number of px, rounded and kept within the draggable range. */
export const normalizeColumnWidth = (width: unknown): number | undefined =>
  typeof width === 'number' && Number.isFinite(width) ? Math.round(Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width))) : undefined;

/** Gives a column the width the user dragged it to, or — for `undefined` — back its derived width. */
export const setColumnWidth = (columns: ViewColumn[], id: string, width: number | undefined): ViewColumn[] =>
  columns.map(column => {
    if (column.id !== id) return column;
    const clean = normalizeColumnWidth(width);
    const { width: _previous, ...rest } = column;
    return clean ? { ...rest, width: clean } : rest;
  });
